import { TrackingProvider, useTracking } from '@act/react-components/tracking';
import SideMenuDrawer from '@act/react-components/SideMenuDrawer/SideMenuDrawer';
import {
  FlexContainer,
  IconLibrary,
  ReemaThemeV2,
  ToggleSwitch,
  useDisplayModeContext,
} from '@act/storybook';
import { FullScreenDialog } from '@act/toolkit/styles/fullScreenDialogComponents';
import { useMobile } from '@act/utilities/mobile-context';
import { useSideMenuDrawer } from '@act/utilities/SideMenuDrawerContext';
import { Box, IconButton } from '@mui/material';
import { useMemo, useState } from 'react';

const CALL_FORM_VIEW = 'callForm';
const GOALS_VIEW = 'goals';

interface MobileToggleSwitchProps {
  selectedSection: string;
  setSelectedSection: (value: string) => void;
}

const MobileToggleSwitch: React.FC<MobileToggleSwitchProps> = ({
  selectedSection,
  setSelectedSection,
}) => {
  const { generateTrackingProps } = useTracking();

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        flexShrink: 0,
        alignSelf: 'flex-start',
      }}
    >
      <ToggleSwitch
        {...generateTrackingProps('mobile-toggle')}
        options={[
          { label: 'Call Notes', value: CALL_FORM_VIEW },
          { label: 'Goals & Actions', value: GOALS_VIEW },
        ]}
        value={selectedSection}
        onChange={setSelectedSection}
      />
    </Box>
  );
};

export const CallFullScreen = ({
  open,
  handleClose,
  leftContent,
  rightContent,
  topContent,
  drawerIsOpen,
  sideMenuContent,
}: {
  open: boolean;
  handleClose: () => void;
  leftContent: React.ReactNode;
  rightContent: React.ReactNode;
  topContent?: React.ReactNode;
  drawerIsOpen?: boolean;
  sideMenuContent?: React.ReactElement;
}) => {
  const { displayMode } = useDisplayModeContext();
  const { isMobile, orientation } = useMobile();
  const { toggleDrawer } = useSideMenuDrawer();
  const [selectedSection, setSelectedSection] = useState(CALL_FORM_VIEW);
  const landscape = useMemo(() => orientation === 'landscape', [orientation]);
  const portrait = useMemo(() => orientation === 'portrait', [orientation]);

  return (
    <TrackingProvider contextName="call-fullscreen">
      {isMobile && !landscape && sideMenuContent && (
        <SideMenuDrawer sideMenuContent={sideMenuContent} />
      )}
      <FullScreenDialog
        open={open}
        onClose={() => handleClose()}
        displayMode={displayMode}
        noElevation={true}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            marginTop: 0,
            gap: isMobile ? 2 : 0,
            ...(isMobile
              ? {
                  backgroundColor:
                    selectedSection === CALL_FORM_VIEW
                      ? ReemaThemeV2.LIGHT_MODE.palette.neutralLight.main
                      : ReemaThemeV2.LIGHT_MODE.palette.creamShades[10],
                }
              : {
                  background: `linear-gradient(to right, ${ReemaThemeV2.LIGHT_MODE.palette.neutralLight.main} 0%, ${ReemaThemeV2.LIGHT_MODE.palette.neutralLight.main} 50%, ${ReemaThemeV2.LIGHT_MODE.palette.creamShades[10]} 50%, ${ReemaThemeV2.LIGHT_MODE.palette.creamShades[10]} 100%)`,
                }),
          }}
        >
          <Box
            sx={{
              padding: isMobile && !landscape ? '8px' : '24px',
              gap: isMobile && landscape ? '24px' : '8px',
              display: 'flex',
            }}
          >
            {isMobile && !landscape && (
              <IconButton
                onClick={toggleDrawer}
                aria-label="Toggle navigation menu"
              >
                <IconLibrary iconName="List" size={24} weight="regular" />
              </IconButton>
            )}
            <Box sx={{ flexGrow: 2 }}>{topContent}</Box>
          </Box>
          {isMobile && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <MobileToggleSwitch
                selectedSection={selectedSection}
                setSelectedSection={setSelectedSection}
              />
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              overflow: !isMobile && !portrait ? 'hidden' : 'scroll',
              marginTop: 1,
            }}
          >
            {(!isMobile || selectedSection === CALL_FORM_VIEW) && (
              <FlexContainer
                direction="column"
                alignItems="center"
                gap={2}
                width={isMobile ? '100%' : '50%'}
                backgroundColor={
                  ReemaThemeV2.LIGHT_MODE.palette.neutralLight.main
                }
              >
                {leftContent}
              </FlexContainer>
            )}
            {(!isMobile || selectedSection === GOALS_VIEW) && (
              <Box
                sx={{
                  gap: 12,
                  width: isMobile ? '100%' : '50%',
                  backgroundColor:
                    ReemaThemeV2.LIGHT_MODE.palette.creamShades[10],
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: isMobile ? 'flex-start' : 'flex-end',
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                  minHeight: '100%',
                  position: 'relative',
                  ...(!drawerIsOpen ? { padding: 3 } : {}),
                }}
              >
                {rightContent}
              </Box>
            )}
          </Box>
        </Box>
      </FullScreenDialog>
    </TrackingProvider>
  );
};
